import type { CustomerAddressDto, ShippingMethodDto } from 'types/api/customer.type';

export function formatCityStateCountry(parts: {
  city?: string | null;
  subdivisionName?: string | null;
  countryName?: string | null;
  zipCode?: string | null;
}): string {
  return [parts.zipCode, parts.city, parts.subdivisionName, parts.countryName]
    .filter(Boolean)
    .join(' — ');
}

/** Order page city line — `zip - city - province code - COUNTRY`, empty parts skipped */
export function formatCityProvinceCodeCountry(parts: {
  city?: string | null;
  subdivisionCode?: string | null;
  countryName?: string | null;
  zipCode?: string | null;
}): string {
  // Country comes as `Italy`; uppercase it to match the ERP-style city / province values.
  return [parts.zipCode, parts.city, parts.subdivisionCode, parts.countryName?.toUpperCase()]
    .filter(Boolean)
    .join(' - ');
}

/** Shipping method description in the UI language (falls back to the other one, then the code). */
export function formatShippingMethod(
  method: ShippingMethodDto | null | undefined,
  language: string
): string {
  if (!method) return '';
  const own = language.startsWith('it') ? method.descriptionIt : method.descriptionEn;
  return own || method.descriptionEn || method.descriptionIt || method.code || '';
}

/**
 * Address row (order page, New / Copy Order modals, PDF): `street number` as in the mockup
 * (`PIAZZA GARIBALDI 2`), then `, line 2`; empty parts skipped.
 */
export function formatCustomerAddressLines(address: CustomerAddressDto): string {
  const street = [address.addressLine1, address.addressNumber].filter(Boolean).join(' ');
  return [street, address.addressLine2].filter(Boolean).join(', ');
}

/** Label for address `<Select>` options — `code - company name - line 1`, empty parts skipped */
export function formatAddressSelectOptionLabel(address: CustomerAddressDto): string {
  return (
    [address.code, address.displayName, address.addressLine1]
      .filter(Boolean)
      .join(' - ')
      .trim() || '—'
  );
}
